import { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Camera, BatteryCharging, Gamepad2, HardDrive, RotateCcw } from "lucide-react";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";

const BUDGETS = [
  { value: 200, label: "Moins de 200 €" },
  { value: 400, label: "200 – 400 €" },
  { value: 700, label: "400 – 700 €" },
  { value: 1500, label: "Plus de 700 €" },
];

const PRIORITIES = [
  { value: "photo", label: "Belle photo", icon: Camera },
  { value: "autonomie", label: "Grande autonomie", icon: BatteryCharging },
  { value: "jeux", label: "Jeux & performance", icon: Gamepad2 },
  { value: "stockage", label: "Beaucoup de stockage", icon: HardDrive },
];

export default function Quiz() {
  const [budget, setBudget] = useState(null);
  const [priorities, setPriorities] = useState([]);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const togglePriority = (v) =>
    setPriorities((p) => (p.includes(v) ? p.filter((x) => x !== v) : [...p, v]));

  const submit = async () => {
    setLoading(true);
    try {
      const r = await api.post("/recommend", { budget, priorities });
      setResults(r.data);
      if (!r.data.length) toast.info("Aucun modèle en stock ne correspond pour le moment.");
    } catch (e) {
      toast.error("Une erreur est survenue. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setBudget(null); setPriorities([]); setResults(null); };

  return (
    <div className="container-oxy max-w-2xl py-10" data-testid="quiz-page">
      <p className="eyebrow text-center">Quiz</p>
      <h1 className="mt-1 text-center font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
        Trouvez <span className="text-gold-dark">votre</span> téléphone
      </h1>
      <p className="mt-2 text-center text-sm text-slate-600">
        Nous ne vous recommandons que des modèles réellement en stock en boutique.
      </p>

      {results === null ? (
        <div className="card-oxy mt-8 p-5 sm:p-8">
          <h2 className="font-display text-lg font-bold text-slate-900">Votre budget maximum</h2>
          <div className="mt-4 grid grid-cols-2 gap-2.5" data-testid="quiz-budget-options">
            {BUDGETS.map((b) => (
              <button key={b.value} data-testid={`quiz-budget-${b.value}`} onClick={() => setBudget(b.value)}
                className={`rounded-2xl border-2 px-4 py-3.5 text-sm font-semibold transition-all ${budget === b.value ? "border-gold bg-gold-soft text-ink" : "border-slate-200 text-slate-700 hover:border-gold/50"}`}>
                {b.label}
              </button>
            ))}
          </div>

          <h2 className="mt-8 font-display text-lg font-bold text-slate-900">Ce qui compte pour vous</h2>
          <div className="mt-4 grid grid-cols-2 gap-2.5" data-testid="quiz-priority-options">
            {PRIORITIES.map((p) => {
              const Icon = p.icon;
              const active = priorities.includes(p.value);
              return (
                <button key={p.value} data-testid={`quiz-priority-${p.value}`} onClick={() => togglePriority(p.value)}
                  className={`flex items-center gap-2.5 rounded-2xl border-2 px-4 py-3.5 text-sm font-semibold transition-all ${active ? "border-gold bg-gold-soft text-ink" : "border-slate-200 text-slate-700 hover:border-gold/50"}`}>
                  <Icon className={`h-5 w-5 ${active ? "text-gold-dark" : "text-slate-400"}`} />
                  {p.label}
                </button>
              );
            })}
          </div>

          <button data-testid="quiz-submit-btn" onClick={submit} disabled={!budget || loading}
            className="btn-gold mt-8 w-full disabled:opacity-50">
            <Sparkles className="h-4 w-4" /> {loading ? "Recherche…" : "Voir mes recommandations"}
          </button>
        </div>
      ) : (
        <div className="mt-8">
          {results.length > 0 ? (
            <>
              <h2 className="text-center font-display text-xl font-bold text-slate-900" data-testid="quiz-results-title">
                Nos {results.length} recommandations en stock
              </h2>
              <div className="mt-6 grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-3">
                {results.map((p) => <ProductCard key={p.id} product={p} />)}
              </div>
            </>
          ) : (
            <div className="card-oxy p-10 text-center" data-testid="quiz-no-result">
              <p className="font-display text-lg font-semibold text-slate-800">Aucun modèle en stock pour ces critères</p>
              <p className="mt-2 text-sm text-slate-500">Passez en boutique ou contactez-nous : de nouveaux modèles arrivent régulièrement.</p>
            </div>
          )}
          <button data-testid="quiz-restart-btn" onClick={reset} className="btn-outline-oxy mx-auto mt-8 flex">
            <RotateCcw className="h-4 w-4" /> Recommencer le quiz
          </button>
        </div>
      )}
    </div>
  );
}
